/**
 * RepsDualPage — Sales Manager dual-tab Performance view.
 *
 * Ported from v2 Claude Design `aura-exec.jsx` → `PageRepsDual`.
 *   Personal tab: my own quota attainment
 *     GET /api/v1/role-kpis/sales/quota-attainment
 *   Team tab: rep leaderboard
 *     GET /api/v1/role-kpis/sales/rep-leaderboard
 *
 * C2 (2026-07-15): the original routes (`/sales/personal/performance`,
 * `/sales/team/leaderboard`) returned 404. Both are repointed to the real
 * `/role-kpis/sales/*` endpoints, which return the canonical KpiPayload
 * `{ value, breakdown, meta }` shape — reconciled into the view models below.
 * The Personal tab surfaces the one real per-scope metric that maps
 * (`meta.attainmentPct`); fields with no honest source render "—" (no fake data).
 *
 * Tab state persists in URL query param `?tab=personal|team`.
 * UI-only · no backend changes · tenant-agnostic (D-09).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageFrame,
  Tabs,
  Kpi,
  DataTable,
  Progress,
  Spark,
  Avatar,
  LoadingBlock,
  ErrorBlock,
  EmptyBlock,
  useFetch,
  compactINR,
  type DataTableRow,
} from '@/components/executive/ExecAtoms';

interface PersonalPerf {
  quotaAttainmentPct?: number;
  rank?: number;
  pipeline?: number;
  pipelineWeighted?: number;
  wonThisQuarter?: number;
  wonYoYPct?: number;
  activityScore?: string;
  callsMade?: number;
  meetingsHeld?: number;
  siteVisits?: number;
  trend?: { calls?: number[]; meetings?: number[]; visits?: number[] };
}

interface LeaderboardRow {
  userId?: string;
  name: string;
  openDeals?: number;
  wonValue?: number;
  quotaPct?: number;
  activities?: number;
}

// Canonical shape returned by every /role-kpis/* endpoint: a headline `value`
// plus a `breakdown` of {label, value} rows (+ optional meta). Both routes this
// page consumes (rep-leaderboard, quota-attainment) return it.
interface KpiBreakdownRow {
  label: string;
  value: number;
}
interface KpiPayload {
  value?: number;
  breakdown?: KpiBreakdownRow[];
  meta?: {
    attainmentPct?: number | null;
    targetRupees?: number | null;
    hasRealTarget?: boolean;
  };
}

const TABS = ['Team', 'Personal'];

export function RepsDualPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab')?.toLowerCase();
  const tab = rawTab === 'personal' ? 'Personal' : 'Team';
  const setTab = useCallback(
    (t: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', t.toLowerCase());
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // C2: repointed to the real endpoints (see file header). Both return KpiPayload.
  const personal = useFetch<KpiPayload>(
    tab === 'Personal' ? '/role-kpis/sales/quota-attainment' : null,
  );
  const team = useFetch<LeaderboardRow[] | { items: LeaderboardRow[] } | KpiPayload>(
    tab === 'Team' ? '/role-kpis/sales/rep-leaderboard' : null,
  );

  const leaderboard = useMemo<LeaderboardRow[]>(() => {
    const d = team.data;
    if (!d) return [];
    if (Array.isArray(d)) return d;
    const items = (d as { items?: LeaderboardRow[] }).items;
    if (Array.isArray(items)) return items;
    // KpiPayload: breakdown rows are { label = rep name, value = won ₹ }.
    const breakdown = (d as KpiPayload).breakdown;
    if (Array.isArray(breakdown)) {
      return breakdown.map((b) => ({ name: b.label, wonValue: b.value }));
    }
    return [];
  }, [team.data]);

  // Personal tab: /sales/quota-attainment returns collected-revenue attainment.
  // Only `meta.attainmentPct` maps to a real per-scope KPI this page renders (the
  // "Quota attainment" card). Every other field has no honest source here, so it
  // stays undefined → the cards render "—". We NEVER synthesize numbers.
  const perf = useMemo<PersonalPerf>(() => {
    const d = personal.data;
    if (!d) return {};
    const pct = d.meta?.attainmentPct;
    return { quotaAttainmentPct: typeof pct === 'number' ? pct : undefined };
  }, [personal.data]);

  const loading = tab === 'Personal' ? personal.loading : team.loading;
  const err = tab === 'Personal' ? personal.error : team.error;

  return (
    <PageFrame
      title="Performance"
      sub="Quota attainment · pipeline · team leaderboard"
    >
      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {loading && <LoadingBlock />}
      {err && err !== 'not-available' && (
        <ErrorBlock message="Could not load performance data." />
      )}
      {err === 'not-available' && (
        <EmptyBlock message="Performance endpoint not available yet." />
      )}

      {!loading && !err && tab === 'Personal' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 10,
          }}
        >
          <Kpi
            label="Quota attainment"
            value={
              typeof perf?.quotaAttainmentPct === 'number'
                ? `${perf?.quotaAttainmentPct}%`
                : '—'
            }
            delta={
              perf?.rank
                ? `Rank #${perf?.rank} on team`
                : undefined
            }
            color="var(--success)"
          />
          <Kpi
            label="Pipeline"
            value={
              typeof perf?.pipeline === 'number'
                ? compactINR(perf?.pipeline)
                : '—'
            }
            delta={
              typeof perf?.pipelineWeighted === 'number'
                ? `${compactINR(perf?.pipelineWeighted)} weighted`
                : undefined
            }
          />
          <Kpi
            label="Closed won (Q)"
            value={
              typeof perf?.wonThisQuarter === 'number'
                ? compactINR(perf?.wonThisQuarter)
                : '—'
            }
            delta={
              typeof perf?.wonYoYPct === 'number'
                ? `${perf?.wonYoYPct > 0 ? '+' : ''}${perf?.wonYoYPct}% YoY`
                : undefined
            }
            color="var(--success)"
          />
          <Kpi
            label="Activity score"
            value={perf?.activityScore ?? '—'}
          />

          <div
            className="card"
            style={{ padding: 14, gridColumn: 'span 4' }}
          >
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                marginBottom: 12,
              }}
            >
              Quarterly trend
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 14,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-3)',
                    marginBottom: 6,
                  }}
                >
                  Calls made
                </div>
                <div
                  style={{
                    fontFamily: 'var(--ff-serif)',
                    fontSize: 24,
                    fontWeight: 500,
                    color: 'var(--ink-0)',
                  }}
                >
                  {perf?.callsMade ?? '—'}
                </div>
                {perf?.trend?.calls && (
                  <Spark
                    values={perf?.trend.calls}
                    w={160}
                    h={32}
                    color="var(--ink-0)"
                  />
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-3)',
                    marginBottom: 6,
                  }}
                >
                  Meetings held
                </div>
                <div
                  style={{
                    fontFamily: 'var(--ff-serif)',
                    fontSize: 24,
                    fontWeight: 500,
                    color: 'var(--ink-0)',
                  }}
                >
                  {perf?.meetingsHeld ?? '—'}
                </div>
                {perf?.trend?.meetings && (
                  <Spark
                    values={perf?.trend.meetings}
                    w={160}
                    h={32}
                    color="var(--saffron-600)"
                  />
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-3)',
                    marginBottom: 6,
                  }}
                >
                  Site visits
                </div>
                <div
                  style={{
                    fontFamily: 'var(--ff-serif)',
                    fontSize: 24,
                    fontWeight: 500,
                    color: 'var(--ink-0)',
                  }}
                >
                  {perf?.siteVisits ?? '—'}
                </div>
                {perf?.trend?.visits && (
                  <Spark
                    values={perf?.trend.visits}
                    w={160}
                    h={32}
                    color="var(--success)"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !err && tab === 'Team' && (
        <>
          {leaderboard.length === 0 ? (
            <EmptyBlock message="No leaderboard data yet." />
          ) : (
            <DataTable
              columns={[
                { k: 'n', label: 'Rep', w: '220px' },
                {
                  k: 'd',
                  label: 'Open deals',
                  w: '110px',
                  align: 'right',
                  mono: true,
                  muted: true,
                },
                {
                  k: 'won',
                  label: 'Won (month)',
                  w: '110px',
                  align: 'right',
                  mono: true,
                },
                { k: 'quota', label: 'Quota', w: '220px' },
                {
                  k: 'calls',
                  label: 'Activities',
                  w: '120px',
                  align: 'right',
                  mono: true,
                  muted: true,
                },
              ]}
              rows={leaderboard.map((r): DataTableRow => ({
                n: () => (
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                    }}
                  >
                    <Avatar name={r.name} size={26} />
                    <span>{r.name}</span>
                  </div>
                ),
                d: r.openDeals ?? '—',
                won:
                  typeof r.wonValue === 'number'
                    ? compactINR(r.wonValue)
                    : '—',
                quota: () => (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Progress
                      value={r.quotaPct ?? 0}
                      color={
                        (r.quotaPct ?? 0) >= 80
                          ? 'var(--success)'
                          : (r.quotaPct ?? 0) >= 60
                            ? 'var(--saffron-600)'
                            : 'var(--danger)'
                      }
                      h={4}
                    />
                    <span className="num" style={{ fontSize: 11 }}>
                      {r.quotaPct ?? 0}%
                    </span>
                  </div>
                ),
                calls: r.activities ?? '—',
              }))}
            />
          )}
        </>
      )}
    </PageFrame>
  );
}

export default RepsDualPage;
